import React, { useState, useEffect } from 'react';
import { api } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { Plus, Search, Edit, Trash2, Phone, Mail, MapPin, Globe, User, Building2, Filter } from 'lucide-react';

const SUPPLIER_CATEGORIES = [
  'Alimentation',
  'Boissons',
  'Entretien',
  'Linge & Textile',
  'Équipements',
  'Services',
  'Travaux',
  'Transport',
  'Autre'
];

export default function SuppliersPage() {
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [showDialog, setShowDialog] = useState(false);
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    category: '',
    contact_name: '',
    phone: '',
    phone2: '',
    email: '',
    address: '',
    city: '',
    website: '',
    notes: ''
  });

  useEffect(() => {
    fetchSuppliers();
  }, [search, filterCategory]);

  const fetchSuppliers = async () => {
    try {
      const params = {};
      if (search) params.search = search;
      if (filterCategory) params.category = filterCategory;
      
      const response = await api.get('/suppliers', { params });
      setSuppliers(response.data);
    } catch (error) {
      toast.error('Erreur lors du chargement des fournisseurs');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (selectedSupplier) {
        await api.put(`/suppliers/${selectedSupplier.id}`, formData);
        toast.success('Fournisseur mis à jour');
      } else {
        await api.post('/suppliers', formData);
        toast.success('Fournisseur créé');
      }
      setShowDialog(false);
      resetForm();
      fetchSuppliers();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erreur');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer ce fournisseur ?')) return;
    try {
      await api.delete(`/suppliers/${id}`);
      toast.success('Fournisseur supprimé');
      fetchSuppliers();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      category: '',
      contact_name: '',
      phone: '',
      phone2: '',
      email: '',
      address: '',
      city: '',
      website: '',
      notes: ''
    });
    setSelectedSupplier(null);
  };

  const openEditDialog = (supplier) => {
    setSelectedSupplier(supplier);
    setFormData({
      name: supplier.name,
      category: supplier.category || '',
      contact_name: supplier.contact_name || '',
      phone: supplier.phone || '',
      phone2: supplier.phone2 || '',
      email: supplier.email || '',
      address: supplier.address || '',
      city: supplier.city || '',
      website: supplier.website || '',
      notes: supplier.notes || ''
    });
    setShowDialog(true);
  };

  const getCategoryColor = (category) => {
    const colors = {
      'Alimentation': 'bg-green-100 text-green-700',
      'Boissons': 'bg-blue-100 text-blue-700',
      'Entretien': 'bg-yellow-100 text-yellow-700',
      'Linge & Textile': 'bg-purple-100 text-purple-700',
      'Équipements': 'bg-orange-100 text-orange-700',
      'Services': 'bg-pink-100 text-pink-700',
      'Travaux': 'bg-red-100 text-red-700',
      'Transport': 'bg-indigo-100 text-indigo-700'
    };
    return colors[category] || 'bg-gray-100 text-gray-700';
  };

  return (
    <div className="space-y-6" data-testid="suppliers-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-semibold">Fournisseurs</h1>
          <p className="text-muted-foreground mt-1">Annuaire des fournisseurs et prestataires</p>
        </div>
        <Button onClick={() => { resetForm(); setShowDialog(true); }} data-testid="add-supplier-btn">
          <Plus className="w-4 h-4 mr-2" />
          Nouveau fournisseur
        </Button>
      </div>

      {/* Search and Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Rechercher par nom, contact ou téléphone..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10"
              />
            </div>
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-muted-foreground" />
              <Select value={filterCategory || 'all'} onValueChange={(v) => setFilterCategory(v === 'all' ? '' : v)}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="Catégorie" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Toutes catégories</SelectItem>
                  {SUPPLIER_CATEGORIES.map(cat => (
                    <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Badge variant="outline" className="self-center">
              {suppliers.length} fournisseur{suppliers.length > 1 ? 's' : ''}
            </Badge>
          </div>
        </CardContent>
      </Card>

      {/* Suppliers Grid */}
      {loading ? (
        <div className="flex justify-center p-8">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
        </div>
      ) : suppliers.length === 0 ? (
        <Card>
          <CardContent className="text-center py-12 text-muted-foreground">
            {search || filterCategory ? 'Aucun fournisseur trouvé' : 'Commencez par ajouter un fournisseur'}
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {suppliers.map((supplier) => (
            <Card key={supplier.id} className="card-metric hover:shadow-md transition-shadow" data-testid={`supplier-card-${supplier.id}`}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                      <Building2 className="w-6 h-6 text-primary" />
                    </div>
                    <div>
                      <h3 className="font-semibold">{supplier.name}</h3>
                      {supplier.category && (
                        <Badge variant="outline" className={`text-xs mt-1 ${getCategoryColor(supplier.category)}`}>
                          {supplier.category}
                        </Badge>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="icon" onClick={() => openEditDialog(supplier)}>
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button variant="ghost" size="icon" className="text-red-500" onClick={() => handleDelete(supplier.id)}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>

                <div className="space-y-2 text-sm">
                  {supplier.contact_name && (
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <User className="w-4 h-4" />
                      <span>{supplier.contact_name}</span>
                    </div>
                  )}
                  {supplier.phone && (
                    <div className="flex items-center gap-2">
                      <Phone className="w-4 h-4 text-muted-foreground" />
                      <a href={`tel:${supplier.phone}`} className="text-primary hover:underline">{supplier.phone}</a>
                      {supplier.phone2 && (
                        <span className="text-muted-foreground">/ {supplier.phone2}</span>
                      )}
                    </div>
                  )}
                  {supplier.email && (
                    <div className="flex items-center gap-2">
                      <Mail className="w-4 h-4 text-muted-foreground" />
                      <a href={`mailto:${supplier.email}`} className="text-primary hover:underline truncate">{supplier.email}</a>
                    </div>
                  )}
                  {(supplier.address || supplier.city) && (
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <MapPin className="w-4 h-4" />
                      <span className="truncate">{[supplier.address, supplier.city].filter(Boolean).join(', ')}</span>
                    </div>
                  )}
                  {supplier.website && (
                    <div className="flex items-center gap-2">
                      <Globe className="w-4 h-4 text-muted-foreground" />
                      <a href={supplier.website.startsWith('http') ? supplier.website : `https://${supplier.website}`} 
                         target="_blank" 
                         rel="noopener noreferrer" 
                         className="text-primary hover:underline truncate">
                        {supplier.website.replace(/^https?:\/\//, '')}
                      </a>
                    </div>
                  )}
                </div>

                {supplier.notes && (
                  <p className="mt-3 text-xs text-muted-foreground border-t pt-2 line-clamp-2">
                    {supplier.notes}
                  </p>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {selectedSupplier ? 'Modifier le fournisseur' : 'Nouveau fournisseur'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label>Nom *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  placeholder="Nom de l'entreprise"
                  data-testid="input-supplier-name"
                />
              </div>
              <div>
                <Label>Catégorie</Label>
                <Select value={formData.category || 'none'} onValueChange={(v) => setFormData({ ...formData, category: v === 'none' ? '' : v })}>
                  <SelectTrigger>
                    <SelectValue placeholder="Sélectionner" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Non défini</SelectItem>
                    {SUPPLIER_CATEGORIES.map(cat => (
                      <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Personne de contact</Label>
                <Input
                  value={formData.contact_name}
                  onChange={(e) => setFormData({ ...formData, contact_name: e.target.value })}
                  placeholder="Nom du contact"
                />
              </div>
              <div>
                <Label>Téléphone principal</Label>
                <Input
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  placeholder="+212 6..."
                />
              </div>
              <div>
                <Label>Téléphone secondaire</Label>
                <Input
                  value={formData.phone2}
                  onChange={(e) => setFormData({ ...formData, phone2: e.target.value })}
                  placeholder="+212 5..."
                />
              </div>
              <div className="col-span-2">
                <Label>Email</Label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="contact@exemple.com"
                />
              </div>
              <div className="col-span-2">
                <Label>Adresse</Label>
                <Input
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  placeholder="Rue, quartier..."
                />
              </div>
              <div>
                <Label>Ville</Label>
                <Input
                  value={formData.city}
                  onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                  placeholder="Marrakech"
                />
              </div>
              <div>
                <Label>Site web</Label>
                <Input
                  value={formData.website}
                  onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                  placeholder="www.exemple.com"
                />
              </div>
              <div className="col-span-2">
                <Label>Notes</Label>
                <Textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  placeholder="Informations complémentaires..."
                  rows={2}
                />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowDialog(false)}>
                Annuler
              </Button>
              <Button type="submit">
                {selectedSupplier ? 'Mettre à jour' : 'Créer'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
